import debounce from "@mui/material/utils/debounce";
import axios from "axios";
import uniqBy from "lodash/uniqBy";
import { useEffect, useMemo, useState } from "react";
import type { FunctionComponent } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { Controller } from "react-hook-form";

import { config } from "shared/data";

import type { StudentFormStaticDataType } from "scmp-app/components/posties/form-drawer/types";
import { PostiesFormLabel } from "scmp-app/components/posties/student-form/form-label";
import { renderStaticField } from "scmp-app/components/posties/student-form/helpers";
import {
  StyledAutocomplete,
  StyledFormControl,
  StyledTextField,
} from "scmp-app/components/posties/student-form/styles";
import type {
  CustomAutoCompleteOption,
  School,
  StudentStaticFields,
  StudentStaticFieldValue,
} from "scmp-app/components/posties/student-form/types";

type Props = {
  formProps: UseFormReturn<FieldValues>;
  itemField: StudentFormStaticDataType & StudentStaticFieldValue;
  itemKey: keyof StudentStaticFields;
};

const otherOptions = { documentId: "other", name: "Other" };
export const SchoolField: FunctionComponent<Props> = ({ formProps, itemField, itemKey }) => {
  const {
    control,
    formState: { errors },
    watch,
  } = formProps;
  const [options, setOptions] = useState<School[]>([]);
  const [inputValue, setInputValue] = useState("");
  const value = watch(itemKey) as CustomAutoCompleteOption;

  const fetch = useMemo(
    () =>
      debounce((request: { input: string }, callback: (results?: School[]) => void) => {
        void axios
          .post(
            config.graphql.studentService.host,
            {
              query: `
              query getSchools($keyword: String!) {
                getSchools(filters: { keyword: $keyword }) {
                  documentId
                  name
                }
              }
            `,
              variables: {
                keyword: request.input,
              },
            },
            {
              headers: {
                apiKey: config.graphql.studentService.apiKey,
                "Content-Type": "application/json",
              },
            },
          )
          .then((response: { data: { data: { getSchools: School[] } } }) =>
            callback(response.data.data.getSchools ?? []),
          );
      }, 400),
    [],
  );

  useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return;
    }

    fetch({ input: inputValue }, (results?: School[]) => {
      if (active) {
        let newOptions: School[] = [];

        if (value) {
          newOptions = [value];
        }

        newOptions = results
          ? uniqBy([...newOptions, ...results, otherOptions], function (item) {
              return item.documentId;
            })
          : [...newOptions, otherOptions];

        setOptions(newOptions);
      }

      return null;
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  if (itemField.type !== "school") return null;

  return (
    <>
      <StyledFormControl fullWidth>
        <PostiesFormLabel helperText={itemField.helperText} title={itemField.title} />
        <Controller
          control={control}
          name={itemKey}
          render={({ field }) => {
            const value = field.value as CustomAutoCompleteOption;

            return (
              <StyledAutocomplete
                {...field}
                disablePortal
                filterOptions={options => options}
                isOptionEqualToValue={(option, value) =>
                  (option as CustomAutoCompleteOption)?.documentId ===
                  (value as CustomAutoCompleteOption).documentId
                }
                noOptionsText="Please type to search"
                onChange={(_event, value) => {
                  const selectedValue = value as CustomAutoCompleteOption;
                  field.onChange(selectedValue);
                }}
                onInputChange={(_event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                options={options}
                popupIcon={null}
                renderInput={parameters => (
                  <StyledTextField
                    {...parameters}
                    error={!!errors[itemKey]}
                    fullWidth
                    helperText={String(errors[itemKey]?.message ?? "")}
                    placeholder={itemField.placeholder ?? ""}
                    value={value?.name ?? null}
                    variant="outlined"
                  />
                )}
                {...itemField.autoCompleteProps}
              />
            );
          }}
        />
      </StyledFormControl>
      {itemField?.others !== undefined &&
        value?.documentId === itemField?.others?.showOptionDocumentId &&
        renderStaticField(itemField.others.field, itemField.others.fieldKey, formProps)}
    </>
  );
};

SchoolField.displayName = "SchoolField";
